import pkceChallenge from "pkce-challenge";

let azureConfig;

export class HyperionAuth {

    async login() {
        await this.reloadEnvironment();

        return new Promise((resolve, reject) => {
            if (this.isAuthenticated()) {
                window.location = azureConfig.homeRedirect;
            } else {
                this.resetHyxAuth();
                localStorage.setItem('hyx-auth-provider', 'azure');

                // no user at all - go sign in.
                this.redirect(azureConfig.signInEndpoint, azureConfig.redirectUri)
            }
        });
    }

    async logout() {
        await axios.post('api/logout').then(function () {
            localStorage.setItem('hyx-auth', "");

            let params = {
                post_logout_redirect_uri: azureConfig.postLogoutRedirectUri,
            };

            let esc = encodeURIComponent;
            let query = Object.keys(params)
                .map(k => esc(k) + '=' + esc(params[k]))
                .join('&');

            window.location = azureConfig.signOutEndpoint + query;
        });
    }

    async initialize(successCallback, failureCallback, autoLogin = false) {
        successCallback = successCallback || this.defaultSuccessCallback;
        failureCallback = failureCallback || this.defaultFailureCallback;

        await this.initialiseEnvironment(failureCallback);

        let urlParams = new URLSearchParams(window.location.search);

        if (this.isAuthenticated()) {
            this.setDefaultHeaders()
        } else if (window.location.pathname === '/auth' || window.location.pathname === '/password-reset') {
            // we're post sign in - validate the code
            if (urlParams.has('error')) {
                this.resetHyxAuth();

                if (!this.checkForAzureErrors(urlParams.get('error_description'))) {
                    if (urlParams.has('error_description')) {
                        failureCallback(urlParams.get('error_description'));
                        return;
                    }
                    failureCallback(urlParams.get('error'));
                }

                return;
            }

            if (window.location.pathname === '/password-reset') {
                window.location = '/login?info=Password+changed+successfully'
            }

            let code = urlParams.get('code');
            let state = urlParams.get('state');

            if (!this.validateStateValue(state)) {
                this.resetHyxAuth();
                failureCallback('State is invalid');
            }

            // state is valid - lets get an access token
            let accessTokenResponse = await this.getAccessToken(code);

            if (accessTokenResponse.data.error) {
                this.resetHyxAuth();
                failureCallback(accessTokenResponse.data.error_description, accessTokenResponse.data.error);

                return;
            }

            localStorage.setItem('hyx-auth', JSON.stringify(accessTokenResponse.data));

            this.setDefaultHeaders();

            successCallback();
        } else {
            if (autoLogin) {
                await this.login();
            }
        }
    }

    async initialiseEnvironment(unauthenticatedCallback) {
        this.setupInterceptors(unauthenticatedCallback);

        let envVars = JSON.parse(localStorage.getItem('hyx-env'));

        if (!envVars) {
            await this.reloadEnvironment();
            envVars = JSON.parse(localStorage.getItem('hyx-env'));
        }

        this.setAzureConfig(envVars);
    }

    checkForAzureErrors(description) {
        if (description.includes('AADB2C90118')) {
            // AADB2C90118: user requests password reset
            // When using sign in and sign up combination flow the redirect is not automatic
            this.redirect(azureConfig.passwordResetEndpoint, azureConfig.passwordResetRedirectUri);
            return true;
        }

        if (description.includes('AADB2C90091')) {
            // AADB2C90091: user cancels password reset
            window.location = '/login';
            return true;
        }

        if (description.includes('AADB2C90273')) {
            // AADB2C90273: HX user refused to grant profile permissions to SOAP application
            this.defaultFailureCallback("Permission to read profile denied by user");
            return true;
        }

        if (description.includes('AADB2C90151')) {
            // AADB2C90151: maximum number of MFA attempts reached
            this.defaultFailureCallback("Maximum number of multi-factor authentication attempts exceeded");
            return true;
        }

        if (description.includes('AADB2C90080') || description.includes('AADB2C90129') || description.includes('AADB2C90128')) {
            // AADB2C90080: refresh token expired
            // AADB2C90129: The account associated with this grant no longer exists
            // AADB2C90128: The provided grant has been revoked
            this.defaultFailureCallback("Your session has expired, please re-authenticate");
            return true;
        }

        if (description.includes('AADB2C90111') || description.includes('AADB2C90114')) {
            // AADB2C90111: Your account has been locked
            // AADB2C90114: Your account is temporarily locked to prevent unauthorized use
            this.defaultFailureCallback("Your account is locked, contact support or try again later");
            return true;
        }

        if (description.includes('AADB2C90157')) {
            // AADB2C90157: User has exceeded the maximum number for retries for a self-asserted step.
            this.defaultFailureCallback("Maximum number of retry attempts exceeded.");
            return true;
        }

        return false;
    }

    async getAccessToken(code, refresh = false) {
        let params = refresh ? {refresh_token: code} : {code: code};

        params.code_verifier = this.getCodeVerifier();

        let esc = encodeURIComponent;
        let query = Object.keys(params)
            .map(k => esc(k) + '=' + esc(params[k]))
            .join('&');

        return await axios.post('/api/token', query, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json'
            }
        });
    }

    getAuthToken() {
        if (localStorage.getItem('hyx-auth')) {
            return JSON.parse(localStorage.getItem('hyx-auth')).access_token
        }

        return '';
    }

    getRefreshToken() {
        if (localStorage.getItem('hyx-auth')) {
            return JSON.parse(localStorage.getItem('hyx-auth')).refresh_token
        }

        return null;
    }

    redirect(authUrl, redirectUri) {
        let params = {
            client_id: azureConfig.clientId,
            response_type: 'code',
            response_mode: 'query',
            redirect_uri: redirectUri,
            scope: azureConfig.clientId + ' offline_access openid',
            state: this.generateStateValue(),
            code_challenge: this.getCodeChallenge(),
            code_challenge_method: 'S256',
        };

        if (azureConfig.stylesUrl) {
            params.styles_url = azureConfig.stylesUrl;
        }

        let esc = encodeURIComponent;
        let query = Object.keys(params)
            .map(k => esc(k) + '=' + esc(params[k]))
            .join('&');

        window.location = authUrl + query;
    }

    setupInterceptors(unauthenticatedCallback) {
        axios.interceptors.request.use(async (config) => {
            if (localStorage.getItem('hyx-auth')) {
                let authData = JSON.parse(localStorage.getItem('hyx-auth'));

                if (authData.refresh_token) {
                    let tokenExpiry = new Date(authData.expires_on * 1000);

                    if (tokenExpiry <= new Date() && config.url !== '/api/token' && config.url !== '/api/environment') {
                        let accessTokenResponse = await this.getAccessToken(authData.refresh_token, true);

                        if (accessTokenResponse.data.error) {
                            localStorage.setItem('hyx-auth', '');

                            if (!this.checkForAzureErrors(accessTokenResponse.data.error)) {
                                this.defaultFailureCallback(accessTokenResponse.data.error);
                                return;
                            }
                        }

                        localStorage.setItem('hyx-auth', JSON.stringify(accessTokenResponse.data));
                        this.setDefaultHeaders();

                        config.headers.Authorization = `Bearer ${accessTokenResponse.data.access_token}`
                    }
                }
            }

            return config;
        });

        axios.interceptors.response.use((response) => {
                return response;
            },
            function (error) {
                // If not logged in, make user login.
                if (error.response.status === 401) {
                    this.resetHyxAuth();

                    if (error.response.data.error) {
                        unauthenticatedCallback(error.response.data.error);
                    } else {
                        unauthenticatedCallback('Unauthenticated');
                    }
                }

                // Do something with response error
                return Promise.reject(error)

            }.bind(this));
    }

    isAuthenticated() {
        return this.getAuthToken();
    }

    setDefaultHeaders() {
        axios.defaults.headers.common['Authorization'] = "Bearer " + this.getAuthToken();
        axios.defaults.headers.common['Authorization-Provider'] = localStorage.getItem('hyx-auth-provider');
    }

    defaultSuccessCallback() {
        window.location = azureConfig.homeRedirect;
    }

    defaultFailureCallback(errorDescription) {
        if (!errorDescription) errorDescription = 'An error has occurred. Please try again later';
        window.location = '/login?error=' + errorDescription;
    }

    generateStateValue() {
        let state = Math.floor(100000000 + Math.random() * 900000000);
        localStorage.setItem('hyx-state', state.toString());

        return state;
    }

    validateStateValue(stateValue) {
        return stateValue === localStorage.getItem('hyx-state');
    }

    getCodeChallenge() {
        let pkce = pkceChallenge();

        // store verifier for later
        localStorage.setItem('hyx-code-verifier', pkce.code_verifier);

        return pkce.code_challenge;
    }

    getCodeVerifier() {
        return localStorage.getItem('hyx-code-verifier');
    }

    async reloadEnvironment() {
        let result = await axios.get('/api/environment');
        localStorage.setItem('hyx-env', JSON.stringify(result.data));

        this.setAzureConfig(result.data);
    }

    getEnvironment() {
        return JSON.parse(localStorage.getItem('hyx-env'));
    }

    setAzureConfig(config) {
        azureConfig = {
            clientId: config.HX_AUTH_CLIENT_ID,
            signInEndpoint: `https://${config.HX_AUTH_AZURE_TENANT}.b2clogin.com/${config.HX_AUTH_AZURE_TENANT}.onmicrosoft.com/B2C_1A_signup_signin_AAD/oauth2/v2.0/authorize?`,
            signOutEndpoint: `https://${config.HX_AUTH_AZURE_TENANT}.b2clogin.com/${config.HX_AUTH_AZURE_TENANT}.onmicrosoft.com/B2C_1A_signup_signin_AAD/oauth2/v2.0/logout?`,
            redirectUri: config.HX_AUTH_REDIRECT_URI,
            passwordResetRedirectUri: config.HX_AUTH_PASSWORD_RESET_REDIRECT_URI,
            postLogoutRedirectUri: config.HX_AUTH_POST_LOGOUT_REDIRECT_URI,
            passwordResetEndpoint: `https://${config.HX_AUTH_AZURE_TENANT}.b2clogin.com/${config.HX_AUTH_AZURE_TENANT}.onmicrosoft.com/B2C_1A_password_reset/oauth2/v2.0/authorize?`,
            homeRedirect: config.HX_AUTH_HOME_REDIRECT,
            stylesUrl: config.HX_AUTH_STYLES_URL,
        }
    }

    getApiUrl() {
        return this.getEnvironment().MIX_APP_API_DOMAIN;
    }

    getUserScopes() {
        let authData = JSON.parse(localStorage.getItem('hyx-auth'));
        if (authData.hasOwnProperty('scope')) {
            return authData.scope.split(' ');
        } else {
            return [];
        }
    }

    resetHyxAuth() {
        localStorage.setItem('hyx-auth', '');
    }
}
